/**
 * Council Trail v0.1 Item 2 — convene-crew orchestration (SP-B hardened).
 *
 * Turns a list of selected persona_ids into a real council session: the first
 * id becomes the primary persona (drives session-create), the rest are invited
 * as peers via inviteCrew (mode='bounded', scope={kind:'whole_session'}). On
 * success it navigates to the trail surface and returns a structured
 * ConveneResult.
 *
 * Fail-loud, never silent (spec §9):
 *   - createPersonaChat failure: toast, no navigation, returns undefined.
 *   - inviteCrew is NOT all-or-nothing — a 200 can carry skipped[] (e.g.
 *     "already in active roster"). Anyone not echoed in invited[] (a skip OR a
 *     batch throw) is reported as `failed` and forwarded in the nav state as
 *     `crewPartial`, so the arrival surface can offer "call them again" — never
 *     swallowed (the 2026-05-05 silent-wipe lesson).
 *
 * isConvening short-circuits re-entry so a double-click on the CTA doesn't fire
 * two session-creates.
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToast } from '../contexts/ToastContext';
import { createPersonaChat } from '../services/chatService';
import { councilApiClient } from '../services/councilApiClient';
import { hillTargetsApiClient } from '../services/hillTargetsApiClient';
import type { TargetRef } from '../types/hill';

export interface ConveneResult {
  sessionId: string;
  primary: string;
  invited: string[];
  failed: string[];
}

export interface ConveneOptions {
  nameById?: Record<string, string>;
  /** SP3b — optional set-out hill target; PUT to the trail slot after session-create. */
  hillTarget?: TargetRef;
}

export interface UseConveneCrewResult {
  convene: (
    personaIds: string[],
    opts?: ConveneOptions,
  ) => Promise<ConveneResult | undefined>;
  isConvening: boolean;
}

export function useConveneCrew(): UseConveneCrewResult {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isConvening, setIsConvening] = useState(false);

  const convene = useCallback(
    async (
      personaIds: string[],
      opts?: ConveneOptions,
    ): Promise<ConveneResult | undefined> => {
      // SP3b — guard lowered to <1 so a solo (n=1) set-out carrying a hill rides
      // this same create→PUT→navigate path. Invite is skipped when there are no peers.
      if (personaIds.length < 1) return undefined;
      if (isConvening) return undefined;
      const nameById = opts?.nameById;
      setIsConvening(true);
      const [primary, ...peers] = personaIds;
      try {
        const session = await createPersonaChat(primary, undefined, { isNew: true, surface: 'trailhead' });
        const sessionId = session.session_id;
        let failed: string[] = [];
        if (peers.length) {
          try {
            const resp = await councilApiClient.inviteCrew(sessionId, {
              persona_ids: peers,
              mode: 'bounded',
              scope: { kind: 'whole_session' },
            });
            // inviteCrew is batch + NOT all-or-nothing: a 200 can carry skipped[].
            // Anyone not echoed in invited[] did not make it onto the trail.
            const invitedIds = ((resp as any)?.invited ?? []).map(
              (e: any) => e.persona_id,
            );
            failed = peers.filter((p) => !invitedIds.includes(p));
          } catch (e) {
            failed = peers; // batch call threw → all peers missed
            showToast(
              `${peers.length} companion${peers.length > 1 ? 's' : ''} didn't make it — call them again from the trail`,
              'error',
            );
          }
        }
        // SP3b — a set-out hill target lands on the trail slot after session-create
        // (solo + crew). Non-fatal: the trail still opens; operator can set it later.
        if (opts?.hillTarget) {
          try {
            await hillTargetsApiClient.putHillTarget(sessionId, {
              scope_kind: 'trail', scope_ref: null, target: opts.hillTarget,
            });
          } catch { /* swallow — the header chip is the recovery path */ }
        }
        // TH-1 — fire the sherpa greeting on this fresh session, UNCONDITIONALLY
        // (no-goal warm-open + solo n=1 paths must greet too). Best-effort: the
        // compose box is the floor if it fails (the FE recovers via PEER_TURN_FAILED).
        try { await councilApiClient.kickoffGreeting(sessionId); }
        catch { /* best-effort; the compose box is the floor */ }
        // F4: forward names (from the caller's byId map) so the arrival banner
        // can name who's missing instead of "a companion". Only the failed
        // ones, and only when the caller supplied names.
        const failedNames: Record<string, string> = {};
        if (nameById) {
          for (const id of failed) {
            if (nameById[id]) failedNames[id] = nameById[id];
          }
        }
        const crewPartial = failed.length
          ? {
              sessionId,
              primary,
              failed,
              ...(Object.keys(failedNames).length ? { failedNames } : {}),
            }
          : undefined;
        navigate(`/trailhead/persona/${primary}/${sessionId}`, {
          state: { ...(crewPartial ? { crewPartial } : {}), greetingPending: true },
        });
        return {
          sessionId,
          primary,
          invited: peers.filter((p) => !failed.includes(p)),
          failed,
        };
      } catch (e) {
        showToast(
          `Convene failed: ${(e as Error).message ?? 'session-create failed'}`,
          'error',
        );
        return undefined;
      } finally {
        setIsConvening(false);
      }
    },
    [navigate, showToast, isConvening],
  );

  return { convene, isConvening };
}
